import React from 'react';
import BigNumber from 'bignumber.js';
import { Dialog, DialogTitle, DialogContent, Typography, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { convertWeiToTokens } from 'utilities';
import { TOKENS } from 'constants/tokens';

interface NetworkRewardBreakdownProps {
  open: boolean;
  onClose: () => void;
  dynamicRewardAmount: BigNumber;
  detailedRecords?: any[];
}

interface UserReleaseRecord {
  userAddress: string;
  level: number;
  releaseAmount: BigNumber;
  releaseTime: Date;
  dailyAmount: BigNumber;
}

const NetworkRewardBreakdown: React.FC<NetworkRewardBreakdownProps> = ({
  open,
  onClose,
  dynamicRewardAmount,
  detailedRecords = [],
}) => {
  // 处理用户释放记录，按时间排序
  const userReleaseRecords = React.useMemo((): UserReleaseRecord[] => {
    console.log('=== Processing User Release Records ===');
    console.log('Raw detailedRecords:', detailedRecords);

    const records: UserReleaseRecord[] = [];
    const threeDaysAgo = new Date();
    threeDaysAgo.setDate(threeDaysAgo.getDate() - 3);

    detailedRecords.forEach((recordWrapper, index) => {
      const record = recordWrapper?.data || recordWrapper;
      console.log(`Processing record ${index}:`, record);

      if (!record || record.active === false) {
        console.log(`Skipping record ${index} - inactive or null`);
        return;
      }

      // 计算释放记录（基于每日释放）
      const startTime = record.startTime ? new Date(record.startTime * 1000) : new Date();
      const releasedDays = record.releasedDays || 0;
      const dailyAmount = record.dailyAmount ? new BigNumber(record.dailyAmount) : new BigNumber(0);
      const level = record.generation || 1;
      const userAddress = record.fromUser || 'Unknown';

      // 生成最近几天的释放记录
      for (let day = 0; day < Math.min(releasedDays, 3); day++) {
        const releaseDate = new Date(startTime);
        releaseDate.setDate(releaseDate.getDate() + releasedDays - day - 1);

        // 只显示最近3天的记录
        if (releaseDate >= threeDaysAgo) {
          records.push({
            userAddress,
            level,
            releaseAmount: dailyAmount,
            releaseTime: releaseDate,
            dailyAmount,
          });
        }
      }
    });

    // 按时间倒序排序（最新的在前）
    const sortedRecords = records.sort((a, b) => b.releaseTime.getTime() - a.releaseTime.getTime());
    console.log('Final sorted records:', sortedRecords);
    console.log('=== End Processing ===');

    return sortedRecords;
  }, [detailedRecords]);

  // 格式化地址显示
  const formatAddress = (address: string): string => {
    if (!address || address === 'Unknown') return 'Unknown';
    return `${address.slice(0, 6)}...${address.slice(-4)}`;
  };

  // 格式化时间显示
  const formatTime = (date: Date): string =>
    date.toLocaleString('zh-CN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      PaperProps={{
        style: {
          borderRadius: '12px',
          margin: '16px',
          maxHeight: '85vh',
          width: '100%',
          position: 'relative',
          backgroundColor: '#2a2d3a',
          color: 'white',
        },
      }}
    >
      <IconButton
        onClick={onClose}
        style={{
          position: 'absolute',
          right: '12px',
          top: '12px',
          color: 'white',
          zIndex: 1,
        }}
      >
        <CloseIcon />
      </IconButton>

      <DialogTitle style={{ textAlign: 'center', padding: '20px 16px 12px', color: 'white' }}>
        <Typography variant="h5" style={{ fontWeight: 'bold', marginBottom: '8px' }}>
          动态收益详情
        </Typography>
        <Typography variant="body2" style={{ color: '#aaa', marginBottom: '8px' }}>
          总动态收益: {dynamicRewardAmount.toFixed(6)} DL
        </Typography>
        <Typography variant="body2" style={{ color: '#aaa' }}>
          最近3天释放记录 • 按时间排序
        </Typography>
      </DialogTitle>

      <DialogContent style={{ padding: '0 16px 16px', backgroundColor: '#2a2d3a' }}>
        {userReleaseRecords.length > 0 ? (
          <div style={{ maxHeight: '60vh', overflowY: 'auto' }}>
            {userReleaseRecords.map((record, index) => (
              <div
                key={`${record.userAddress}-${record.level}-${record.releaseTime.getTime()}`}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: '12px 0',
                  borderBottom: index < userReleaseRecords.length - 1 ? '1px solid #3a3d4a' : 'none',
                  gap: '12px',
                }}
              >
                {/* 用户头像/层级标识 */}
                <div
                  style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '50%',
                    backgroundColor: '#4a5568',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                    fontSize: '12px',
                    fontWeight: 'bold',
                    flexShrink: 0,
                  }}
                >
                  L{record.level}
                </div>

                {/* 用户信息和释放详情 */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '4px' }}>
                    <div>
                      <div style={{ color: 'white', fontSize: '14px', fontWeight: '500', marginBottom: '2px' }}>
                        {formatAddress(record.userAddress)}
                      </div>
                      <div style={{ color: '#aaa', fontSize: '12px' }}>
                        L{record.level}层推荐收益
                      </div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ color: 'white', fontSize: '16px', fontWeight: 'bold' }}>
                        {convertWeiToTokens({
                          valueWei: record.releaseAmount,
                          token: TOKENS.dl,
                        }).toFixed(6)}
                      </div>
                      <div style={{ color: '#aaa', fontSize: '12px' }}>
                        DL
                      </div>
                    </div>
                  </div>

                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={{ color: '#aaa', fontSize: '12px' }}>
                      释放时间
                    </div>
                    <div style={{ color: '#aaa', fontSize: '12px' }}>
                      {formatTime(record.releaseTime)}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div
            style={{
              textAlign: 'center',
              padding: '40px 20px',
              color: '#aaa',
            }}
          >
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>
              <span role="img" aria-label="chart">📊</span>
            </div>
            <Typography variant="h6" style={{ marginBottom: '8px', color: 'white' }}>
              暂无释放记录
            </Typography>
            <Typography variant="body2" style={{ color: '#aaa' }}>
              最近3天内没有动态收益释放记录
            </Typography>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default NetworkRewardBreakdown;
